import React from 'react';
import { ChevronDown, Info } from 'lucide-react';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger
} from '../ui/dropdown-menu';
import ResponsiveTooltip from '../ui/responsive-tooltip';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { DESDE_EL_INICIO, PeriodoEvolucion } from '../../utils/resumenMulticuenta/evolucion';

export interface FiltroPlanesProps {
	planes: { id: string; nombre: string }[];
	seleccion: string[];
	onCambiar: (seleccion: string[]) => void;
	/** `'todo'` («Desde el inicio») o un año. */
	periodo: PeriodoEvolucion;
	/** Los años con datos, del más reciente al más antiguo. */
	anios: string[];
	onPeriodo: (periodo: PeriodoEvolucion) => void;
	translate: (clave: string) => string;
}

/**
 * Los dos selectores del Resumen (`FilterRow` de Proa): el año y los planes. Afectan a todo lo que
 * hay debajo en su columna (cifras, gráfica, barra y movimientos), no a las cards de la derecha
 * (Marisa, 21/10/2026). El de planes nunca se queda vacío: desmarcar el último no hace nada; con un
 * solo plan no se enseña, porque no hay nada que elegir.
 */
const FiltroPlanes: React.FC<FiltroPlanesProps> = ({
	planes,
	seleccion,
	onCambiar,
	periodo,
	anios,
	onPeriodo,
	translate
}) => {
	const alternar = (id: string) => {
		const siguiente = seleccion.includes(id)
			? seleccion.filter((elegido) => elegido !== id)
			: [...seleccion, id];
		if (siguiente.length > 0) onCambiar(siguiente);
	};

	return (
		<div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
			<div className="flex items-center gap-1">
				<p className="text-sm text-muted-foreground">{translate('resumen.filter.label')}</p>
				<ResponsiveTooltip content={translate('resumen.filter.help')}>
					<button
						type="button"
						aria-label={translate('resumen.filter.helpLabel')}
						className="relative inline-flex cursor-help before:absolute before:left-1/2 before:top-1/2 before:size-11 before:-translate-x-1/2 before:-translate-y-1/2"
					>
						<Info className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
					</button>
				</ResponsiveTooltip>
			</div>
			{/* grid-cols-2 en móvil, como Proa: los dos selectores en una fila. */}
			<div className="grid grid-cols-2 gap-2 sm:flex sm:w-auto sm:items-center">
				<Select value={periodo} onValueChange={onPeriodo}>
					<SelectTrigger className="w-full sm:w-40" aria-label={translate('resumen.chart.year')}>
						<SelectValue />
					</SelectTrigger>
					<SelectContent>
						<SelectItem value={DESDE_EL_INICIO}>{translate('resumen.chart.sinceStart')}</SelectItem>
						{anios.map((anio) => (
							<SelectItem key={anio} value={anio}>
								{anio}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
				{planes.length > 1 && (
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<button
								type="button"
								className="flex h-11 w-full min-w-0 items-center justify-between gap-2 rounded-control border border-input bg-background px-3 text-sm sm:w-auto"
							>
								{/* El conteo primero: en móvil es lo que sobrevive al recorte. */}
								<span className="truncate">
									{translate('resumen.filter.count')
										.replace('{elegidos}', String(seleccion.length))
										.replace('{total}', String(planes.length))}
								</span>
								<ChevronDown className="h-4 w-4 shrink-0 opacity-50" aria-hidden="true" />
							</button>
						</DropdownMenuTrigger>
						<DropdownMenuContent align="end">
							<DropdownMenuLabel>{translate('resumen.filter.title')}</DropdownMenuLabel>
							<DropdownMenuSeparator />
							{planes.map((plan) => (
								<DropdownMenuItem
									key={plan.id}
									// Sin esto el menú se cierra en cada marca, y aquí se marcan varios seguidos.
									onSelect={(evento) => evento.preventDefault()}
									onClick={() => alternar(plan.id)}
									className="gap-2"
								>
									<Checkbox checked={seleccion.includes(plan.id)} className="pointer-events-none" />
									{plan.nombre}
								</DropdownMenuItem>
							))}
							<DropdownMenuSeparator />
							<Button
								variant="ghost"
								size="sm"
								className="w-full justify-start px-2"
								onClick={() => onCambiar(planes.map((plan) => plan.id))}
							>
								{translate('resumen.filter.all')}
							</Button>
						</DropdownMenuContent>
					</DropdownMenu>
				)}
			</div>
		</div>
	);
};

export default FiltroPlanes;
